import { AgGridAngular } from '@ag-grid-community/angular';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material';
import { agGridLocale, EurCurrencyCellRenderer } from 'app/utils/ag-grid.locale';
import slugify from 'slugify';

import { CubeRow } from '../cube.component';
import { CompanySubset } from 'app/api/company.api.service';

export interface ExcelCell {
  data: ExcelData;
  // Optional style to apply
  styleId?: string;
  // Optional The number of _additional_ cells to span across, so
  // 1 means that the cell will span 2 columns
  mergeAcross?: number;
}

export interface ExcelData {
  // Excel data type. Case sensitive.
  type: 'String' | 'Number' | 'Boolean' | 'DateTime' | 'Error';
  value: string | null;
}

export const ExcelStyles = [
  {
    id: 'title',
    font: {
      color: '#ffffff',
      size: 16,
    },
    alignment: {
      horizontal: 'Left',
      vertical: 'Bottom'
    },
    interior: {
      color: '#00897b',
      pattern: 'Solid'
    }
  },
  {
    id: 'title_bold',
    font: {
      color: '#ffffff',
      bold: true,
      size: 16,
    },
    alignment: {
      horizontal: 'Left',
      vertical: 'Bottom'
    },
    interior: {
      color: '#00897b',
      pattern: 'Solid'
    }
  },
  {
    id: 'header',
    font: {
      color: '#00897b',
      bold: true,
    },
  },
  {
    id: 'excelString',
    dataType: 'string',
  },
  {
    id: 'excelNumberNoDecimal',
    numberFormat: {
      format: '# ##0'
    }
  }
];

@Component({
  selector: 'cube-detail-dialog',
  templateUrl: './cube-detail-dialog.component.html',
  styleUrls: ['./cube-detail-dialog.component.scss']
})
export class CubeDetailDialogComponent implements OnInit {
  public subTitle: string;

  public gridApi;
  public gridColumnApi;

  public columnDefs;
  public defaultColDef;
  public autoGroupColumnDef;
  public sideBar;
  public statusBar;
  public rowBuffer;
  public localeText;
  public rowClassRules;
  public pivotMode;
  public modules;
  public popupParent;
  public excelStyles;

  @ViewChild('agGridCubeDetail', { static: false }) agGrid: AgGridAngular;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      company: CompanySubset,
      title: string, labels:
      string[],
      rows: CubeRow[]
    }) {
    this.subTitle = data.labels.length > 1
      ? data.labels.slice(0, data.labels.length - 1).join(' ➜ ') + ' ' + data.labels[data.labels.length - 1]
      : data.labels.join('');

    this.modules = AllModules;
    this.popupParent = document.getElementById('body');
    this.localeText = agGridLocale.fr;
    this.defaultColDef = {
      width: 150,
      enableValue: true,
      enableRowGroup: true,
      enablePivot: false,
      sortable: true,
      filter: true,
      resizable: true,
      enableCharts: false
    };
    this.columnDefs = [
      { headerName: 'Date', field: 'entryDate', cellClass: 'excelString' },
      { headerName: 'Journal', field: 'originalJournal', width: 60, cellClass: 'excelString' },
      { headerName: 'Libellé', field: 'entryLabel', cellClass: 'excelString' },
      {
        headerName: 'Débit',
        field: 'debit',
        type: 'numericColumn',
        cellRenderer: EurCurrencyCellRenderer,
        cellClass: 'excelNumberNoDecimal'
      },
      {
        headerName: 'Crédit',
        field: 'credit',
        type: 'numericColumn',
        cellRenderer: EurCurrencyCellRenderer,
        cellClass: 'excelNumberNoDecimal'
      },
      { headerName: 'Compte', field: 'account', cellClass: 'excelString' },
      { headerName: 'Auxiliaire', field: 'auxiliary', cellClass: 'excelString' },
      { headerName: 'Lib Compte', field: 'accountLabel', cellClass: 'excelString' },
    ];
    this.sideBar = null;
    this.statusBar = {
      statusPanels: [
        {
          statusPanel: 'agTotalRowCountComponent',
          align: 'left'
        },
        {
          statusPanel: 'agAggregationComponent'
        },
        { statusPanel: 'agSelectedRowCountComponent' },
        { statusPanel: 'agFilteredRowCountComponent' }
      ]
    };
    this.pivotMode = false;
    this.excelStyles = ExcelStyles;
  }

  ngOnInit(): void { }

  onGridReady(params): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  onFirstDataRendered(params): void {
    this.autoSizeAll(params);
  }

  private autoSizeAll(params): void {
    const allColumnIds = params.columnApi
      .getAllColumns()
      .map(column => column.colId);
    setTimeout(() => params.columnApi.autoSizeColumns(allColumnIds), 0);
  }

  public exportExcel(): void {
    const params = this.getExportParams();
    this.gridApi.exportDataAsExcel(params);
  }

  private getFileName(extension: string): string {
    slugify.extend({
      '&': 'et'
    });
    const slug = slugify(this.data.labels.join(' '), {
      replacement: '-',
      remove: /[^a-zA-Z0-9\s\-]/g,
      lower: true
    });
    return `${this.data.company.id}-${slug}.${extension}`;
  }

  private getCustomHeader(): ExcelCell[][] {
    const header: ExcelCell[][] = [
      [
        {
          data: {
            type: 'String',
            value: this.data.title,
          },
          mergeAcross: 1,
          styleId: 'title_bold'
        },
        {
          data: {
            type: 'String',
            value: this.subTitle, // .replace('➜', '>')
          },
          mergeAcross: 5,
          styleId: 'title'
        }
      ]
    ];
    return header;
  }

  private getExportParams(): any {
    return {
      customHeader: this.getCustomHeader(),
      fileName: this.getFileName('xlsx'),
      allColumns: true,
      sheetName: this.data.company.name,
      exportMode: 'xlsx'
    };
  }

}
